/**
 * ArchiveDashboard — archive volume + lifecycle overview (drafts/57 ARC-04).
 * Active volume, by-type breakdown, pending-approval, destruction-due (≤90d),
 * legal-hold and destroyed counts; the destruction-due queue links to detail.
 * Route: /archive.
 */
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardBody, CardHeader, PageHero, Button } from '@edms/design-system';
import { apiFetch } from '../../api/client';
import { fmtDate, type ArchiveDashboardData } from './types';

function Stat({ label, value, bg, fg, onClick }: { label: string; value: number; bg: string; fg: string; onClick?: () => void }) {
  return (
    <div onClick={onClick} style={{ background: bg, color: fg, borderRadius: 12, padding: '14px 18px', minWidth: 140, cursor: onClick ? 'pointer' : 'default', border: `1px solid ${fg}22` }}>
      <div style={{ fontSize: 12, opacity: 0.85 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

export function ArchiveDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const q = useQuery<ArchiveDashboardData>({
    queryKey: ['archive', 'dashboard'],
    queryFn: ({ signal }) => apiFetch<ArchiveDashboardData>('/api/v1/archive/dashboard', { signal }),
    staleTime: 20_000,
  });

  const goRecords = (search: { status?: string; legal_hold?: string; record_type?: string } = {}) =>
    navigate({ to: '/archive/records', search });

  return (
    <div style={{ padding: 'var(--space-6, 24px)', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHero
        eyebrow={t('archive.dashboard.eyebrow', { defaultValue: 'Archive & Records' })}
        title={t('archive.dashboard.title', { defaultValue: 'Archive & Records' })}
        subtitle={t('archive.dashboard.subtitle', { defaultValue: 'GMP records archive — intake, retention, legal hold and destruction, on the controlled-document backbone.' })}
        actions={
          <Button variant="secondary" size="lg" onClick={() => navigate({ to: '/archive/records' })}>
            {t('archive.dashboard.openRecords', { defaultValue: 'Open records / retrieval' })}
          </Button>
        }
      />

      {q.isLoading && <p role="status">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
      {q.isError && <p role="alert" style={{ color: '#B91C1C' }}>{t('archive.dashboard.error', { defaultValue: 'Could not load the archive dashboard.' })}</p>}

      {q.data && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Stat label={t('archive.dashboard.total', { defaultValue: 'Total records' })} value={q.data.summary.total} bg="#EEF2FF" fg="#3730A3" onClick={() => goRecords()} />
            <Stat label={t('archive.dashboard.active', { defaultValue: 'Active' })} value={q.data.summary.active} bg="#DCFCE7" fg="#0E7C5A" onClick={() => goRecords({ status: 'ACTIVE' })} />
            <Stat label={t('archive.dashboard.pendingApproval', { defaultValue: 'Pending approval' })} value={q.data.summary.pending_approval} bg="#FEF3C7" fg="#92400E" onClick={() => goRecords({ status: 'PENDING_APPROVAL' })} />
            <Stat label={t('archive.dashboard.destructionDue', { defaultValue: 'Destruction due ≤90d' })} value={q.data.summary.destruction_due_90} bg="#FFEDD5" fg="#9A3412" />
            <Stat label={t('archive.dashboard.legalHold', { defaultValue: 'On legal hold' })} value={q.data.summary.legal_hold} bg="#FEE2E2" fg="#B91C1C" onClick={() => goRecords({ legal_hold: 'true' })} />
            <Stat label={t('archive.dashboard.destroyed', { defaultValue: 'Destroyed' })} value={q.data.summary.destroyed} bg="#E5E7EB" fg="#374151" onClick={() => goRecords({ status: 'DESTROYED' })} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(360px, 2fr)', gap: 18, alignItems: 'start' }}>
            <Card>
              <CardHeader><h2 style={{ margin: 0, fontSize: 16 }}>{t('archive.dashboard.byType', { defaultValue: 'Records by type' })}</h2></CardHeader>
              <CardBody>
                {q.data.by_type.length === 0 ? <p style={{ color: '#6B7989' }}>—</p> : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {q.data.by_type.map((row) => {
                      const max = Math.max(...q.data!.by_type.map((x) => x.n), 1);
                      return (
                        <li key={row.record_type} onClick={() => goRecords({ record_type: row.record_type })} title={t('archive.dashboard.filterByType', { defaultValue: 'Show {{type}} records', type: row.record_type })} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 28px', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                          <span style={{ fontSize: 13 }}>{row.record_type}</span>
                          <span style={{ background: '#E0E7FF', borderRadius: 6, height: 10, width: `${(row.n / max) * 100}%`, minWidth: 6, display: 'inline-block' }} />
                          <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{row.n}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader><h2 style={{ margin: 0, fontSize: 16 }}>{t('archive.dashboard.destructionQueue', { defaultValue: 'Destruction due (≤ 90 days)' })}</h2></CardHeader>
              <CardBody>
                {q.data.destruction_due.length === 0 ? (
                  <p style={{ color: '#6B7989' }}>{t('archive.dashboard.noneDue', { defaultValue: 'No records are within 90 days of retention end.' })}</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#6B7989', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ padding: '8px 10px' }}>{t('archive.dashboard.col.record', { defaultValue: 'Record' })}</th>
                        <th style={{ padding: '8px 10px' }}>{t('archive.dashboard.col.type', { defaultValue: 'Type' })}</th>
                        <th style={{ padding: '8px 10px' }}>{t('archive.dashboard.col.retentionEnd', { defaultValue: 'Retention end' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.data.destruction_due.map((r) => (
                        <tr key={r.archive_record_id} onClick={() => navigate({ to: '/archive/records/$id', params: { id: r.archive_record_id } })} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                          <td style={{ padding: '10px' }}>{r.title}</td>
                          <td style={{ padding: '10px' }}>{r.record_type}</td>
                          <td style={{ padding: '10px' }}>{fmtDate(r.retention_end_at, i18n.language)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
